import { Button, Form, Input, Space } from "antd";
import Modal from "../../../../components/generic/Modal";
import { useCallback, useEffect, useMemo, useState } from "react";
import MemberSelect from "../../../../components/shared/MemberSelect";
import { useDispatch, useSelector } from "react-redux";
import { addEditTask } from "../../../../store/tasks/thunks";
import { selectSelectedTenantId } from "../../../../store/user/selectors";
import Comments from "../Comments";
import { StyledCreateTaskModal } from "./styles";
import { MentionInput } from "../../../../components/shared/Mentions";
import {
  convertIdsToMentions,
  convertMentionsToIds,
} from "../../../../components/shared/Mentions/helper";
import TaskTabs from "../Activities/TaskTabs";
import { useTaskCases } from "../../../../hooks/useTaskCases";
import Select from "../../../../components/generic/Select";

const CreateTaskModal = ({ onCancel, columns, users, selectedTask }) => {
  const [form] = Form.useForm();

  const tenant_id = useSelector(selectSelectedTenantId);

  const { casesOptions } = useTaskCases();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  const isEdit = useMemo(() => !!selectedTask?.id, [selectedTask]);

  useEffect(() => {
    if (selectedTask?.column_id) {
      form.setFieldsValue({
        ...selectedTask,
        description: convertIdsToMentions(
          selectedTask?.description || "",
          users,
        ),
      });
    }
  }, [selectedTask, users]);

  const handleFinish = useCallback(
    async (values) => {
      try {
        setIsSubmitting(true);
        await dispatch(
          addEditTask({
            ...values,
            description: convertMentionsToIds(values?.description, users),
            tenant_id,
            old_column_id: selectedTask?.column_id || undefined,
            id: selectedTask?.id || undefined,
          }),
        ).unwrap();
        onCancel();
      } catch (error) {
        console.log(
          "🚀 ~ CreateTaskModal.jsx:12 ~ handleFinish ~ error:",
          error,
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [tenant_id, selectedTask, users],
  );

  const columnsOptions = useMemo(
    () =>
      Object.keys(columns)?.map((key) => ({
        label: columns[key].name,
        value: key,
      })),
    [columns],
  );

  return (
    <Modal
      centered
      open
      onCancel={onCancel}
      onOk={() => form.submit()}
      width={isEdit ? "100%" : 600}
      isProcessing={isSubmitting}
      footer={null}
    >
      <StyledCreateTaskModal>
        <div className={`${isEdit ? "left-div" : "w-100"}`}>
          <h3 className="task-title">
            {isEdit
              ? `Edit Task #${selectedTask?.display_id ?? ""}`
              : "Create Task"}
          </h3>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            disabled={isSubmitting}
          >
            <Form.Item
              label="Title"
              name="title"
              rules={[
                {
                  required: true,
                  message: "Please enter task title",
                },
              ]}
            >
              <Input placeholder="Enter task title" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <MentionInput
                placeholder="Enter task description"
                users={users}
                disabled={isSubmitting}
              />
            </Form.Item>

            <Form.Item
              label="Column"
              name="column_id"
              rules={[
                {
                  required: true,
                  message: "Please select a column",
                },
              ]}
            >
              <Select placeholder="Select column" options={columnsOptions} />
            </Form.Item>

            <Form.Item
              label="Assign Users"
              name="assigned_ids"
              rules={[
                {
                  required: true,
                  message: "Please select at least one user",
                },
              ]}
            >
              <MemberSelect
                users={users}
                placeholder="Select users"
                mode="multiple"
              />
            </Form.Item>

            <Form.Item label="Cases" name="case_id">
              <Select
                placeholder="Select case"
                options={casesOptions}
                allowClear
              />
            </Form.Item>

            <Form.Item className="flex justify-end">
              <Space>
                <Button onClick={onCancel}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={isSubmitting}>
                  Save
                </Button>
              </Space>
            </Form.Item>
          </Form>
          {isEdit && <TaskTabs task={selectedTask} tenant_id={tenant_id} />}
        </div>
        {isEdit && (
          <div className="right-div">
            <Comments
              selectedTask={selectedTask}
              tenant_id={tenant_id}
              users={users}
            />
          </div>
        )}
      </StyledCreateTaskModal>
    </Modal>
  );
};

export default CreateTaskModal;
